import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileSpreadsheet, Plus, Search, Filter, Eye, ClipboardList, FileText, Sparkles, Wand2, MessageSquare } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { AIButton, AIBadge, AIPanel, AIExplanationBox } from '../components/AIComponents';

// Quote Life sub-navigation
const quoteSubNav = [
  { path: '/quotes', label: 'Quotes', icon: ClipboardList },
  { path: '/quotes/estimates', label: 'Estimates', icon: FileText },
  { path: '/quotes/proposals', label: 'Proposals', icon: FileSpreadsheet },
];

const mockProposals = [
  { id: 1, client: 'Corporate HQ Ltd', title: 'Full Office Renovation', amount: 85000, status: 'draft', date: '2024-01-16' },
  { id: 2, client: 'Retail Group', title: 'Shop Fit-out Package', amount: 42000, status: 'sent', date: '2024-01-14' },
  { id: 3, client: 'Smith Holdings', title: 'Residential Development', amount: 120000, status: 'viewed', date: '2024-01-12' },
  { id: 4, client: 'Jones Estates', title: 'Property Maintenance Contract', amount: 35000, status: 'accepted', date: '2024-01-08' },
];

export function Proposals() {
  const location = useLocation();

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Proposals</h1>
          <p>Create compelling proposals within Quote Life</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" disabled><Plus size={16} />New Proposal</button>
        </div>
      </div>

      {/* Sub Navigation - Proposals inside Quote Life */}
      <div className="flex gap-sm mb-xl">
        {quoteSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      <div className="responsive-grid-4" style={{ marginBottom: 'var(--space-xl)' }}>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-muted)' }}>{mockProposals.filter(p => p.status === 'draft').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Drafts</div>
        </GlassPanel>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-warning)' }}>{mockProposals.filter(p => p.status === 'sent').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Sent</div>
        </GlassPanel>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--accent-primary)' }}>{mockProposals.filter(p => p.status === 'viewed').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Viewed</div>
        </GlassPanel>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-success)' }}>{mockProposals.filter(p => p.status === 'accepted').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Accepted</div>
        </GlassPanel>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) var(--space-md)', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)' }}>
          <Search size={16} style={{ color: 'var(--text-dimmed)' }} />
          <input type="text" placeholder="Search proposals..." style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none' }} />
        </div>
        <button className="btn btn-secondary"><Filter size={16} />Filter</button>
      </div>

      <GlassPanel title="All Proposals" titleIcon={FileSpreadsheet}>
        {mockProposals.length > 0 ? (
          <div className="list-container">
            {mockProposals.map((proposal) => (
              <div key={proposal.id} className="glass-panel" style={{ marginBottom: 'var(--space-sm)', padding: 'var(--space-md)' }}>
                <Link to={`/quotes/proposals/${proposal.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="list-item-content">
                      <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        {proposal.title}
                        <AIBadge text="AI" />
                      </div>
                      <div className="list-item-subtitle">{proposal.client} • {proposal.date}</div>
                    </div>
                    <div className="list-item-meta">
                      {proposal.status === 'viewed' && <Eye size={14} style={{ color: 'var(--accent-primary)' }} />}
                      <span className={`status-badge ${proposal.status === 'accepted' ? 'completed' : proposal.status === 'viewed' ? 'active' : proposal.status === 'sent' ? 'pending' : 'draft'}`}>{proposal.status}</span>
                      <span className="list-item-amount">£{proposal.amount.toLocaleString()}</span>
                    </div>
                  </div>
                </Link>
                
                {/* AI Actions per proposal */}
                <div className="quote-card-ai-actions">
                  <AIButton icon={Wand2} tooltip="AI generation will be enabled soon">
                    Generate proposal content
                  </AIButton>
                  <AIButton icon={MessageSquare} tooltip="AI generation will be enabled soon">
                    Rewrite in simpler language
                  </AIButton>
                  <AIButton icon={Sparkles} tooltip="AI generation will be enabled soon">
                    Tailor for this client
                  </AIButton>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={FileSpreadsheet} title="No proposals yet" description="Create your first proposal to impress clients" />
        )}
      </GlassPanel>

      <AIExplanationBox>
        AI helps create professional proposals by generating compelling content, suggesting layouts, and tailoring language for each client.
      </AIExplanationBox>
    </div>
  );
}

export default Proposals;
